import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Route, Router, Routes } from '@angular/router';
import { AppConfig, LocalStorageKeys } from '@mkp/shared/util-app-config';
import { isNavigationStart } from '@mkp/shared/util-router';
import { authenticatedRoutes, visitorUrls } from '@mkp/marketplace-client/routes';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RedirectService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // This service redirects the user that were trying to access an authenticated route while being logged-off
  // Case 1 - redirect to login
  // For example: user is not logged-in but tries to access /vacancy
  // RedirectService will
  //  - save the target route `/vacancy` in localstorage
  //  - navigate to /login
  // after logging-in, a router.effect will
  // - read the target route in localstorage
  // - clear the localstorage value
  // - navigate to the target route
  // Case 2 - redirect to magic link expired
  // If user is trying to login with a magic link and the link that is expired or invalid
  // RedirectService will redirect to /magic-link-expired
  handleRedirect(): void {
    this.router.events
      .pipe(filter(isNavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        // Parse pathname and search params directly from event.url
        const [pathname, search] = event.url.split('?');
        const searchParams = new URLSearchParams(search || '');

        if (isPasswordlessLoginLinkExpired(searchParams.get('error_description'))) {
          this.router.navigate([
            AppConfig.routes.passwordlessLogin,
            AppConfig.routes.passwordlessLoginLinkExpired,
          ]);
        } else if (isRedirectablePath(pathname)) {
          this.redirectToPasswordlessLogin(event.url);
        }
      });
  }

  private redirectToPasswordlessLogin(url: string): void {
    localStorage.setItem(LocalStorageKeys.redirectUri, url);
    this.router.navigate([AppConfig.routes.passwordlessLogin]);
  }
}
const exceptionPaths = ['**', 'logout', ...Object.values(visitorUrls)];
const isRedirectablePath = (path: string): boolean => {
  const redirectablePaths = getRouteFirstFragments(authenticatedRoutes)
    .filter((path) => !exceptionPaths.includes(path))
    .map((path) => `/${path.replace(RegExp(`:[^/]+`), `[^/]+`)}`);

  // check if the path starts with one of the first fragment of the redirectable routes
  return redirectablePaths.some((redirectablePath) => path.match(RegExp(`^${redirectablePath}`)));
};

const getRouteFirstFragments = (routes: Routes): string[] =>
  routes.reduce(
    (acc: string[], route: Route) => [
      ...acc,
      ...(route.path === ''
        ? // when no path: look for children
          route.children
          ? // recursively get the paths of the children
            getRouteFirstFragments(route.children)
          : // no path and no children: it's the dashboard, ignore it
            []
        : // otherwise get the path of the route
          [route.path as string]),
    ],
    []
  );

export const passwordlessLoginLinkExpiredErrors = Object.freeze(
  new Set([
    'Wrong email or verification code.',
    "You've reached the maximum number of attempts. Please try to login again.",
    'The verification code has expired. Please try to login again.',
  ])
);

const isPasswordlessLoginLinkExpired = (errorDescription: string): boolean => {
  return passwordlessLoginLinkExpiredErrors.has(errorDescription);
};
